/**
 * Studio Video Page – single video view with Netflix-style player and comments.
 * Layout similar to post thread: player at top, title/description/views, then comments.
 */

import {useCallback, useMemo, useRef, useState} from 'react'
import {
  Image,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {type CommonNavigatorParams} from '#/lib/routes/types'
import {cleanError} from '#/lib/strings/errors'
import {useSession} from '#/state/session'
import {useProfileQuery} from '#/state/queries/profile'
import {
  useStudioVideoQuery,
  useStudioVideoCommentsQuery,
  useStudioCreateVideoComment,
} from '#/state/queries/studio'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {studioRecordView} from '#/lib/api/studio'
import type {StudioVideo, StudioVideoComment} from '#/lib/api/studio'
import {formatViewCount} from '#/lib/strings/view-count'
import {useNavigation} from '@react-navigation/native'
import {UserAvatar} from '#/view/com/util/UserAvatar'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'StudioVideo'>

const ASPECT_RATIO = 16 / 9

function NetflixStylePlayer({
  videoPath,
  posterUri,
  onReached20Seconds,
}: {
  videoPath: string
  posterUri: string | null
  onReached20Seconds?: () => void
}) {
  const t = useTheme()
  const videoRef = useRef<HTMLVideoElement>(null)
  const reportedRef = useRef(false)

  if (Platform.OS === 'web') {
    return (
      <View
        style={[
          a.overflow_hidden,
          {backgroundColor: '#000', width: '100%', aspectRatio: ASPECT_RATIO},
        ]}>
        <video
          ref={videoRef}
          src={videoPath}
          poster={posterUri ?? undefined}
          controls
          playsInline
          preload="metadata"
          onTimeUpdate={() => {
            if (reportedRef.current || !onReached20Seconds) return
            const ct = videoRef.current?.currentTime
            if (ct != null && ct >= 20) {
              reportedRef.current = true
              onReached20Seconds()
            }
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: '#000',
          }}
        />
      </View>
    )
  }

  // Native: use expo-video when available, else thumbnail + open in browser
  return <NetflixStylePlayerNative videoPath={videoPath} posterUri={posterUri} t={t} />
}

function NetflixStylePlayerNative({
  videoPath,
  posterUri,
  t,
}: {
  videoPath: string
  posterUri: string | null
  t: ReturnType<typeof useTheme>
}) {
  const expoVideo = useMemo(() => {
    try {
      return require('expo-video')
    } catch {
      return null
    }
  }, [])
  const player = useMemo(() => {
    if (!expoVideo) return null
    return expoVideo.createVideoPlayer(videoPath, (p: {loop: boolean}) => {
      p.loop = false
    })
  }, [expoVideo, videoPath])

  if (expoVideo && player) {
    const {VideoView} = expoVideo
    return (
      <View
        style={[
          a.overflow_hidden,
          {backgroundColor: '#000', width: '100%', aspectRatio: ASPECT_RATIO},
        ]}>
        <VideoView
          player={player}
          style={{width: '100%', height: '100%'}}
          nativeControls={true}
        />
      </View>
    )
  }

  // Fallback: poster + tap to open URL
  return (
    <Pressable
      style={[
        a.overflow_hidden,
        {
          backgroundColor: '#000',
          width: '100%',
          aspectRatio: ASPECT_RATIO,
        },
      ]}
      onPress={() => Linking.openURL(videoPath).catch(() => {})}>
      {posterUri ? (
        <Image
          source={{uri: posterUri}}
          style={{width: '100%', height: '100%'}}
          resizeMode="contain"
        />
      ) : (
        <View
          style={[
            a.flex_1,
            a.justify_center,
            a.align_center,
            t.atoms.bg_contrast_50,
          ]}>
          <Text style={[a.text_md, t.atoms.text]}>
            <Trans>Tap to play</Trans>
          </Text>
        </View>
      )}
    </Pressable>
  )
}

function CommentRow({comment}: {comment: StudioVideoComment}) {
  const t = useTheme()
  const handle = comment.author_handle || comment.author_id.slice(0, 8) + '…'
  const date = new Date(comment.created_at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return (
    <View
      style={[
        a.p_lg,
        a.border_t,
        t.atoms.border_contrast_low,
        t.atoms.bg,
      ]}>
      <View style={[a.flex_row, a.align_center, a.gap_sm, a.pb_xs]}>
        <Text style={[a.text_sm, a.font_semi_bold, t.atoms.text]}>
          {handle}
        </Text>
        <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>{date}</Text>
      </View>
      <Text style={[a.text_md, t.atoms.text]}>{comment.text}</Text>
    </View>
  )
}

const DESCRIPTION_COLLAPSED_LINES = 3
const DESCRIPTION_SHOW_MORE_CHARS = 120

export function StudioVideoPage({route, navigation}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const nav = useNavigation()
  const videoId = route.params?.videoId ?? ''
  const [commentText, setCommentText] = useState('')
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)

  const {data: video, isLoading, isError, error, refetch} = useStudioVideoQuery(videoId)
  const {currentAccount} = useSession()
  const {data: comments = []} = useStudioVideoCommentsQuery(videoId)
  const createComment = useStudioCreateVideoComment(videoId)
  const ownerDid = video?.owner_did
  const {data: creatorProfile} = useProfileQuery({
    did: ownerDid,
    staleTime: 60 * 1000,
  })

  const onReached20Seconds = useCallback(() => {
    studioRecordView(videoId).then(() => refetch()).catch(() => {})
  }, [videoId, refetch])

  const onSubmitComment = useCallback(async () => {
    const text = commentText.trim()
    if (!text || createComment.isPending) return
    try {
      await createComment.mutateAsync({
        text,
        author_id: currentAccount?.did,
        author_handle: currentAccount?.handle,
      })
      setCommentText('')
    } catch (_) {
      // Error surfaced via mutation state if needed
    }
  }, [commentText, createComment, currentAccount])

  if (!videoId) {
    return (
      <Layout.Screen>
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>Video</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
        <Layout.Center>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            <Trans>Invalid video</Trans>
          </Text>
        </Layout.Center>
      </Layout.Screen>
    )
  }

  if (isLoading && !video) {
    return (
      <Layout.Screen>
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>Video</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
        <Layout.Center>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            <Trans>Loading…</Trans>
          </Text>
        </Layout.Center>
      </Layout.Screen>
    )
  }

  if (isError || !video) {
    return (
      <Layout.Screen>
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>Video</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
        <Layout.Center>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            {cleanError(error) || _(msg`Video not found`)}
          </Text>
        </Layout.Center>
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen testID="studioVideoPage">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText numberOfLines={1}>
            {video.title || _(msg`Video`)}
          </Layout.Header.TitleText>
        </Layout.Header.Content>
      </Layout.Header.Outer>

      <ScrollView
        style={[a.flex_1]}
        contentContainerStyle={[a.pb_2xl]}
        keyboardShouldPersistTaps="handled">
        <Layout.Center style={[a.w_full]}>
          {/* Netflix-style player: responsive, full width, 16:9 */}
          <View style={[a.w_full, {maxHeight: Platform.OS === 'web' ? '70vh' : undefined}]}>
            <NetflixStylePlayer
              videoPath={video.video_path}
              posterUri={video.thumbnail_path}
              onReached20Seconds={onReached20Seconds}
            />
          </View>

          {/* Creator: avatar + name, link to profile */}
          {ownerDid ? (
            <Pressable
              onPress={() =>
                nav.navigate('Profile' as never, {
                  name: creatorProfile?.handle ?? ownerDid,
                } as never)
              }
              style={({pressed}) => [
                a.flex_row,
                a.align_center,
                a.gap_sm,
                a.p_lg,
                a.border_t,
                t.atoms.border_contrast_low,
                {opacity: pressed ? 0.7 : 1},
              ]}
              accessibilityRole="link"
              accessibilityLabel={_(msg`View creator profile`)}
              accessibilityHint={creatorProfile?.displayName ?? creatorProfile?.handle ?? ownerDid}>
              <UserAvatar
                type="user"
                size={36}
                avatar={creatorProfile?.avatar}
              />
              <Text
                style={[a.text_md, a.font_semi_bold, t.atoms.text]}
                numberOfLines={1}>
                {creatorProfile?.displayName ??
                  creatorProfile?.handle ??
                  (ownerDid.slice(0, 12) + '…')}
              </Text>
            </Pressable>
          ) : null}

          {/* Meta: title, description, views (like post) */}
          <View style={[a.p_lg, a.border_t, t.atoms.border_contrast_low]}>
            <Text style={[a.text_xl, a.font_bold, t.atoms.text, a.pb_xs]}>
              {video.title || _(msg`Untitled`)}
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium, a.pb_sm]}>
              {formatViewCount(video.total_views)} <Trans>views</Trans>
            </Text>
            {video.description ? (
              <View style={[a.gap_xs]}>
                <Text
                  style={[a.text_md, t.atoms.text_contrast_high]}
                  numberOfLines={
                    descriptionExpanded
                      ? undefined
                      : DESCRIPTION_COLLAPSED_LINES
                  }>
                  {video.description}
                </Text>
                {video.description.length > DESCRIPTION_SHOW_MORE_CHARS ? (
                  <Pressable
                    onPress={() =>
                      setDescriptionExpanded(prev => !prev)
                    }
                    accessibilityRole="button"
                    accessibilityLabel={
                      descriptionExpanded
                        ? _(msg`Show less`)
                        : _(msg`Show more`)
                    }>
                    <Text
                      style={[
                        a.text_sm,
                        a.font_semi_bold,
                        t.atoms.text_contrast_medium,
                      ]}>
                      {descriptionExpanded ? (
                        <Trans>Show less</Trans>
                      ) : (
                        <Trans>Show more</Trans>
                      )}
                    </Text>
                  </Pressable>
                ) : null}
              </View>
            ) : null}
          </View>

          {/* Comments */}
          <View style={[a.pt_md]}>
            <Text
              style={[
                a.text_lg,
                a.font_semi_bold,
                t.atoms.text,
                a.px_lg,
                a.pb_md,
              ]}>
              <Trans>Comments</Trans> ({comments.length})
            </Text>
            {comments.length === 0 && !createComment.isPending ? (
              <Text
                style={[
                  a.text_sm,
                  t.atoms.text_contrast_medium,
                  a.p_lg,
                  a.border_t,
                  t.atoms.border_contrast_low,
                ]}>
                <Trans>No comments yet. Be the first to comment.</Trans>
              </Text>
            ) : (
              comments.map(c => <CommentRow key={c.id} comment={c} />)
            )}

            {/* Comment composer: input then Post button below (styled like Studio Watch button) */}
            <View
              style={[
                a.p_lg,
                a.gap_sm,
                a.border_t,
                t.atoms.border_contrast_low,
                t.atoms.bg,
              ]}>
              <TextInput
                placeholder={_(msg`Add a comment…`)}
                placeholderTextColor={t.atoms.text_contrast_medium.color}
                value={commentText}
                onChangeText={setCommentText}
                multiline
                maxLength={500}
                style={[
                  a.w_full,
                  a.text_md,
                  t.atoms.text,
                  a.rounded_md,
                  t.atoms.bg_contrast_25,
                  a.p_md,
                  {minHeight: 44, maxHeight: 120},
                ]}
              />
              <View style={[a.flex_row, a.justify_end]}>
                <Button
                  label={_(msg`Post`)}
                  size="small"
                  color="primary"
                  onPress={onSubmitComment}
                  disabled={!commentText.trim() || createComment.isPending}>
                  <ButtonText>
                    <Trans>Post</Trans>
                  </ButtonText>
                </Button>
              </View>
            </View>
          </View>
        </Layout.Center>
      </ScrollView>
    </Layout.Screen>
  )
}
